import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { AlertCircle, CheckCircle2, Loader2, Database, Wrench, BarChart3 } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';

const AdminDatabaseTools = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const getAuthToken = () => {
    // Get admin token from localStorage
    return localStorage.getItem('adminToken');
  };

  const callAPI = async (endpoint, method = 'GET') => {
    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Admin token not found. Please log out and log back in.');
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/fixes${endpoint}`,
        {
          method: method,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || `Error: ${response.status}`);
      }

      setResults(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const [lessonId, setLessonId] = useState('');

  const diagnoseE5 = () => callAPI('/diagnose-e5-session', 'GET');
  const fixE5 = () => callAPI('/fix-e5-session', 'POST');
  const diagnoseLesson = () => {
    if (!lessonId.trim()) {
      setError('Please enter a lesson ID');
      return;
    }
    callAPI(`/diagnose-lesson/${lessonId.trim()}`, 'GET');
  };
  const fixLesson = () => {
    if (!lessonId.trim()) {
      setError('Please enter a lesson ID');
      return;
    }
    callAPI(`/fix-lesson/${lessonId.trim()}`, 'POST');
  };
  const rebuildLesson = () => {
    if (!lessonId.trim()) {
      setError('Please enter a lesson ID');
      return;
    }
    callAPI(`/rebuild-lesson/${lessonId.trim()}`, 'POST');
  };
  const diagnoseActivities = () => {
    if (!lessonId.trim()) {
      setError('Please enter a lesson ID');
      return;
    }
    callAPI(`/diagnose-lesson-activities/${lessonId.trim()}`, 'GET');
  };
  const fixActivities = () => {
    if (!lessonId.trim()) {
      setError('Please enter a lesson ID');
      return;
    }
    callAPI(`/fix-lesson-activities/${lessonId.trim()}`, 'POST');
  };
  const recalculateStreaks = () => callAPI('/recalculate-all-streaks', 'POST');

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5" />
            Database Diagnostic & Fix Tools
          </CardTitle>
          <CardDescription>
            Use these tools to diagnose and fix production database issues
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Ephesians 5 Session Tools */}
          <div className="p-4 border rounded-lg space-y-3">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <Wrench className="w-4 h-4" />
              Ephesians 5 Session Fix
            </h3>
            <p className="text-sm text-gray-600">
              Fixes: Session showing 5 lessons instead of 4, Day 3 blank screen issues
            </p>
            <div className="flex gap-3">
              <Button
                onClick={diagnoseE5}
                disabled={loading}
                variant="outline"
                className="flex items-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertCircle className="w-4 h-4" />}
                Diagnose Ephesians 5
              </Button>
              <Button
                onClick={fixE5}
                disabled={loading}
                className="flex items-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Fix Ephesians 5
              </Button>
            </div>
          </div>

          {/* Individual Lesson Fix */}
          <div className="p-4 border rounded-lg space-y-3">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <Wrench className="w-4 h-4" />
              Fix Individual Lesson (Day 3 Issue)
            </h3>
            <p className="text-sm text-gray-600">
              Diagnose and fix a specific lesson that's not rendering properly (missing required fields)
            </p>
            <div className="space-y-2">
              <input
                type="text"
                value={lessonId}
                onChange={(e) => setLessonId(e.target.value)}
                placeholder="Enter lesson ID (e.g., Day 3: E5)"
                className="w-full px-3 py-2 border rounded-md"
              />
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Button
                    onClick={diagnoseLesson}
                    disabled={loading || !lessonId.trim()}
                    variant="outline"
                    className="flex items-center gap-2 flex-1"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertCircle className="w-4 h-4" />}
                    Diagnose Lesson
                  </Button>
                  <Button
                    onClick={diagnoseActivities}
                    disabled={loading || !lessonId.trim()}
                    variant="outline"
                    className="flex items-center gap-2 flex-1"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertCircle className="w-4 h-4" />}
                    Check Activities
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={fixActivities}
                    disabled={loading || !lessonId.trim()}
                    variant="outline"
                    className="flex items-center gap-2 flex-1"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    Fix Activities
                  </Button>
                  <Button
                    onClick={rebuildLesson}
                    disabled={loading || !lessonId.trim()}
                    className="flex items-center gap-2 flex-1"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
                    Rebuild Lesson
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Orphaned Lessons Fix */}
          <div className="p-4 border rounded-lg space-y-3 bg-yellow-50">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-yellow-600" />
              Fix Orphaned Lessons
            </h3>
            <p className="text-sm text-gray-600">
              Fixes lessons that have session_id set but don't appear in their session's list.
              Example: "day 20 sample" and "day 21 sample" assigned to Ephesians 5 but not showing.
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => callAPI('/diagnose-orphaned-lessons', 'GET')}
                disabled={loading}
                variant="outline"
                className="flex items-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertCircle className="w-4 h-4" />}
                Diagnose Orphaned Lessons
              </Button>
              <Button
                onClick={() => callAPI('/fix-orphaned-lessons?fix_type=add', 'POST')}
                disabled={loading}
                className="flex items-center gap-2 bg-blue-600"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Add to Sessions
              </Button>
              <Button
                onClick={() => callAPI('/fix-orphaned-lessons?fix_type=clear', 'POST')}
                disabled={loading}
                variant="outline"
                className="flex items-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Make Unassigned
              </Button>
            </div>
          </div>

          {/* Streak Tools */}
          <div className="p-4 border rounded-lg space-y-3">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Streak Counter Fix
            </h3>
            <p className="text-sm text-gray-600">
              Recalculates and updates streak counters for all users based on their completion history
            </p>
            <Button
              onClick={recalculateStreaks}
              disabled={loading}
              variant="outline"
              className="flex items-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />}
              Recalculate All Streaks
            </Button>
          </div>

          {/* Error Display */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Results Display */}
          {results && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <h3 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" />
                Results
              </h3>
              <pre className="text-sm bg-white p-3 rounded border overflow-auto max-h-96">
                {JSON.stringify(results, null, 2)}
              </pre>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Session Cleanup Tools */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5" />
            Session Cleanup Tools
          </CardTitle>
          <CardDescription>
            Diagnose and fix issues with study sessions (whitespace, phantom sessions, etc.)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Button
              onClick={() => callAPI('/diagnose-sessions')}
              disabled={loading}
              variant="outline"
              className="w-full flex items-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
              Diagnose All Sessions
            </Button>

            <Button
              onClick={() => callAPI('/cleanup-whitespace-sessions', 'POST')}
              disabled={loading}
              variant="outline"
              className="w-full flex items-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
              Clean Whitespace in Session IDs
            </Button>

            <div className="pt-4 border-t">
              <p className="text-sm text-gray-600 mb-3">
                <strong>Force Delete Phantom Session:</strong> Use this if a session appears in the UI but can't be deleted normally.
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Session ID (e.g., eph_book_analysis)"
                  className="flex-1 px-3 py-2 border rounded"
                  id="force-delete-session-id"
                />
                <Button
                  onClick={() => {
                    const sessionId = document.getElementById('force-delete-session-id').value;
                    if (sessionId && window.confirm(`Force delete session "${sessionId}"?`)) {
                      callAPI(`/force-delete-session/${encodeURIComponent(sessionId)}`, 'POST');
                    }
                  }}
                  disabled={loading}
                  variant="destructive"
                  className="flex items-center gap-2"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertCircle className="w-4 h-4" />}
                  Force Delete
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>How to Use</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div>
            <strong>Step 1: Fix Session</strong>
            <p className="text-gray-600">Click "Diagnose Ephesians 5" to see the session details. Click "Fix Ephesians 5" to correct the lesson order.</p>
          </div>
          <div>
            <strong>Step 2: Fix Day 3 Lesson</strong>
            <p className="text-gray-600">
              If Day 3 still shows blank: 
              <br/>• Enter the lesson ID (e.g., "Day 3: E5 - Ephesians 5:6-9")
              <br/>• Click "Diagnose" to see issues
              <br/>• If no issues shown but still broken, click "Rebuild Lesson" (safe - preserves content, recreates data structure)
            </p>
          </div>
          <div>
            <strong>Step 3: Verify</strong>
            <p className="text-gray-600">Clear browser cache and check the Ephesians 5 session. All lessons should now load properly.</p>
          </div>
          <div className="pt-2 border-t">
            <strong>Note:</strong>
            <p className="text-gray-600">These tools only affect the database you're currently connected to. Changes made here will be reflected immediately in your application.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDatabaseTools;
